import type {
  PermitIndex,
  PermitIndexCondition,
  PermitIndexID,
  PermitPageSizeAndOrientation,
  TaskCategory,
  TaskGenerator,
} from '../types/compliance.ts';
import { geminiConvertBoundingBoxToPdfCoordinates } from './pdf.ts';
import { baseDomain } from './config.ts';
import { pageRangeToPageNumbers } from './page-ranges.ts';
import type { Facility } from '../types/common.ts';
import type { TenantID } from '../../cross-app-shared/types/db.ts';

/**
 * Parses a PermitIndex's pageFilter into a Set of allowed page numbers.
 * If no filter is set, all pages (1 through pageMax) are included.
 * Applies rangeToInclude first, then subtracts rangeToExclude.
 */
export function parsePermitPageFilter(pageFilter: PermitIndex['pageFilter'], pageMax: number): Set<number> {
  if (!pageFilter) {
    const allPages = new Set<number>();
    for (let i = 1; i <= pageMax; i++) {
      allPages.add(i);
    }
    return allPages;
  }

  const pagesToInclude = pageRangeToPageNumbers(pageFilter.rangeToInclude, pageMax);
  if (pagesToInclude.size === 0) {
    for (let page = 1; page <= pageMax; page++) {
      pagesToInclude.add(page);
    }
  }

  for (const page of pageRangeToPageNumbers(pageFilter.rangeToExclude, pageMax)) {
    pagesToInclude.delete(page);
  }

  return pagesToInclude;
}

export function permitIndexToTaskGeneratorPermit(
  tenantID: TenantID,
  permitIndexFesFile: PermitIndex['file'],
): NonNullable<TaskGenerator['permit']> {
  return {
    fileID: permitIndexFesFile.fileID,
    category: permitIndexFesFile.categories[0] ?? 'Other',
    name: permitIndexFesFile.name,
    url: `https://compliance.${baseDomain}/download/${tenantID}/${permitIndexFesFile.fileID}`,
  };
}

const fileCategoryToTaskCategory: Record<string, TaskCategory> = {
  'Air Permit': 'Air',
  'Air Quality Construction Permit': 'Air',
  'Air Quality Construction Permit(s) or Authorizations': 'Air',
  'Air Quality Operating Permit': 'Air',
  'Air Quality Operating Permit(s) or Authorizations': 'Air',
  'Construction Stormwater Permit': 'Water',
  'Groundwater/Aquifer Discharge Permits': 'Water',
  'Industrial Stormwater Permit': 'Water',
  Miscellaneous: 'Other',
  'NPDES Direct Discharge Permit': 'Water',
  'NPDES Indirect Discharge Permit': 'Water',
  'NPDES Pesticide General Permits': 'Water',
  'Risk Management Plan': 'Air',
  'Stormwater Pollution Prevention Plan': 'Water',
};

export function permitIndexToTaskCategory(permitIndexFesFile: PermitIndex['file']): TaskCategory {
  return fileCategoryToTaskCategory[permitIndexFesFile.categories[0]] ?? 'Other';
}

export function permitIndexConditionSort(a: PermitIndexCondition, b: PermitIndexCondition): number {
  if (a.page !== b.page) {
    return a.page - b.page;
  } else if (a.boundingBox && b.boundingBox && a.boundingBox.yMax !== b.boundingBox.yMax) {
    return a.boundingBox.yMax - b.boundingBox.yMax;
  } else if (a.reference !== b.reference) {
    return a.reference.localeCompare(b.reference);
  } else {
    return a.conditionID.localeCompare(b.conditionID);
  }
}

/**
 * Used when linking renewed permits with their previously indexed incarnations, copies over a subset of fields from
 * old->new to retain more consistency.
 */
export function permitConditionCopyOldToNew({
  oldCondition,
  newCondition,
}: {
  oldCondition: PermitIndexCondition;
  newCondition: PermitIndexCondition;
}): void {
  newCondition.inspectionType = oldCondition.inspectionType;
  newCondition.taskDescription = oldCondition.taskDescription;
  newCondition.taskAssociation = JSON.parse(JSON.stringify(oldCondition.taskAssociation));
}

export function permitConditionToAdobeAnnotation(
  condition: PermitIndexCondition,
  sizesAndOrientation: PermitPageSizeAndOrientation | undefined,
): {
  id: string;
  boundingBox: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };
  page: number;
  comment: string;
} | null {
  if (!condition.boundingBox) {
    return null;
  }

  // Clamp Gemini coordinates to the valid 0-1000 range before converting to PDF coords.
  // Moved note annotations can produce out-of-range values:
  const clampedBB = {
    xMin: Math.max(0, Math.min(condition.boundingBox.xMin, 1000)),
    yMin: Math.max(0, Math.min(condition.boundingBox.yMin, 1000)),
    xMax: Math.max(0, Math.min(condition.boundingBox.xMax, 1000)),
    yMax: Math.max(0, Math.min(condition.boundingBox.yMax, 1000)),
  };

  const pdfBoundingBox = geminiConvertBoundingBoxToPdfCoordinates(clampedBB, sizesAndOrientation);

  return {
    id: condition.conditionID,
    boundingBox: pdfBoundingBox,
    page: condition.page,
    comment: `${condition.reference}: ${condition.taskDescription}`,
  };
}

export function permitIndexingIDCompute(permit: { facilityID: Facility['facilityID']; sha1: string }): PermitIndexID {
  return `permit_index_${permit.facilityID}_${permit.sha1}`;
}
